import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import axiosInstance from "../config/axios";
import {
  canRemoveListingComplianceItem,
  LISTING_COMPLIANCE_DEAL_OPTIONS,
  listingComplianceStatusFromChecklist,
  newListingComplianceItemId,
  normalizeListingComplianceChecklist,
  normalizeListingDealType,
} from "../constants/listingCompliance";
import ComplianceDeadlineInput from "./ComplianceDeadlineInput";
import { normalizeComplianceDeadline } from "../utils/listingComplianceDeadline";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    fontSize: "0.875rem",
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": { borderColor: "#1d4ed8" },
  }),
  menu: (base) => ({ ...base, fontSize: "0.875rem", zIndex: 20 }),
};

function listingOptionLabel(listing) {
  const n = listing.listingNumber;
  const sn = listing.address?.streetNumber || "";
  const st = listing.address?.streetName || "";
  const addr = [sn, st].filter(Boolean).join(" ");
  return addr ? `#${n} — ${addr}` : `#${n}`;
}

const ListingComplianceTab = ({ onSaved }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [dealType, setDealType] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [removedPresetIds, setRemovedPresetIds] = useState([]);
  const [complianceDeadline, setComplianceDeadline] = useState("");
  const [draft, setDraft] = useState("");

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/listings");
      const sorted = (res.data || []).sort(
        (a, b) => b.listingNumber - a.listingNumber,
      );
      setListings(sorted);
    } catch (e) {
      console.error(e);
      toast.error("Could not load listings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  const listingOptions = useMemo(
    () =>
      listings.map((l) => ({
        value: l._id,
        label: listingOptionLabel(l),
      })),
    [listings],
  );

  const selectedListing = useMemo(
    () => listings.find((l) => l._id === selectedId) || null,
    [listings, selectedId],
  );

  const selectedOption = useMemo(
    () => listingOptions.find((o) => o.value === selectedId) || null,
    [listingOptions, selectedId],
  );

  const loadListingCompliance = useCallback((listing) => {
    if (!listing) {
      setDealType("");
      setChecklist([]);
      setRemovedPresetIds([]);
      setComplianceDeadline("");
      return;
    }
    const type = normalizeListingDealType(listing.compliance?.dealType);
    const removed = listing.compliance?.removedPresetIds || [];
    setDealType(type);
    setRemovedPresetIds(removed);
    setComplianceDeadline(
      normalizeComplianceDeadline(listing.compliance?.deadline),
    );
    setChecklist(
      normalizeListingComplianceChecklist(
        listing.compliance?.checklist,
        type,
        removed,
      ),
    );
  }, []);

  const handleListingSelect = (option) => {
    const id = option?.value || "";
    setSelectedId(id);
    const listing = listings.find((l) => l._id === id);
    loadListingCompliance(listing);
  };

  const handleDealTypeChange = (value) => {
    const type = normalizeListingDealType(value);
    setDealType(type);
    setRemovedPresetIds([]);
    setChecklist(normalizeListingComplianceChecklist([], type, []));
  };

  const toggleItem = (id) => {
    setChecklist((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  const addCustomItem = () => {
    const label = draft.trim();
    if (!label) return;
    setChecklist((prev) => [
      ...prev,
      {
        id: newListingComplianceItemId(),
        label,
        checked: false,
        preset: false,
        removable: true,
        group: "custom",
      },
    ]);
    setDraft("");
  };

  const removeItem = (id) => {
    const item = checklist.find((i) => i.id === id);
    if (item?.preset && item.removable) {
      setRemovedPresetIds((prev) => [...prev, id]);
    }
    setChecklist((prev) => prev.filter((i) => i.id !== id));
  };

  const computedStatus = useMemo(
    () => listingComplianceStatusFromChecklist(checklist),
    [checklist],
  );

  const mandatoryItems = checklist.filter((i) => i.group === "mandatory");
  const optionalItems = checklist.filter(
    (i) => i.group === "optional" || i.group === "custom",
  );

  const handleSave = async () => {
    if (!selectedListing?._id) {
      toast.error("Select a listing first.");
      return;
    }
    if (!dealType) {
      toast.error("Select lease or sale.");
      return;
    }
    setSaving(true);
    const status = listingComplianceStatusFromChecklist(checklist);
    try {
      const res = await axiosInstance.patch(
        `/listings/${selectedListing._id}/compliance`,
        {
          dealType,
          checklist,
          status,
          removedPresetIds,
          deadline: complianceDeadline,
        },
      );
      setListings((prev) =>
        prev.map((l) => (l._id === res.data._id ? res.data : l)),
      );
      loadListingCompliance(res.data);
      toast.success("Listing compliance saved.");
      onSaved?.();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save listing compliance.");
    } finally {
      setSaving(false);
    }
  };

  const renderItem = (item) => (
    <li key={item.id} className="flex items-start gap-2">
      <input
        type="checkbox"
        id={`listing-compliance-${item.id}`}
        checked={!!item.checked}
        onChange={() => toggleItem(item.id)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-900 focus:ring-blue-700"
      />
      <label
        htmlFor={`listing-compliance-${item.id}`}
        className="text-sm text-gray-800 flex-1 leading-snug cursor-pointer"
      >
        {item.label}
      </label>
      {canRemoveListingComplianceItem(item) && (
        <button
          type="button"
          onClick={() => removeItem(item.id)}
          className="text-xs text-red-600 hover:underline shrink-0"
        >
          Remove
        </button>
      )}
    </li>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Compliance</h2>
      <p className="text-sm text-gray-500 mb-6">
        Select a listing, choose lease or sale, set a compliance deadline, and
        tick required forms. Status appears in the List table.
      </p>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Listing
          </label>
          <Select
            inputId="listing-compliance-select"
            options={listingOptions}
            value={selectedOption}
            onChange={handleListingSelect}
            isClearable
            isSearchable
            placeholder="Search or select a listing…"
            styles={selectStyles}
            classNamePrefix="listing-compliance"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-t border-gray-100">
          <label
            htmlFor="listing-compliance-deal-type"
            className="text-sm font-medium text-gray-700"
          >
            Lease or sale
          </label>
          <select
            id="listing-compliance-deal-type"
            value={dealType}
            onChange={(e) => handleDealTypeChange(e.target.value)}
            disabled={!selectedId}
            className="block w-full sm:w-56 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700 disabled:bg-gray-100"
          >
            {LISTING_COMPLIANCE_DEAL_OPTIONS.map((o) => (
              <option key={o.value || "empty"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-t border-gray-100">
          <label
            htmlFor="listing-compliance-deadline"
            className="text-sm font-medium text-gray-700"
          >
            Compliance deadline
          </label>
          <ComplianceDeadlineInput
            id="listing-compliance-deadline"
            value={complianceDeadline}
            onChange={setComplianceDeadline}
            disabled={!selectedId}
            className="block w-full sm:w-56 rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-700 focus:ring-blue-700 disabled:bg-gray-100"
          />
        </div>

        <div className="flex justify-between items-center py-2 border-t border-gray-100">
          <span className="text-sm text-gray-700">Compliance status</span>
          <span
            className={`text-sm font-semibold px-2 py-1 rounded-full ${
              computedStatus === "Completed"
                ? "bg-green-100 text-green-800"
                : computedStatus === "Pending"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-gray-100 text-gray-700"
            }`}
          >
            {computedStatus}
          </span>
        </div>

        {!dealType ? (
          <p className="text-sm text-gray-500 border-t border-gray-100 pt-4">
            Select lease or sale to show the required forms checklist.
          </p>
        ) : (
          <>
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Mandatory
              </p>
              <ul className="space-y-2">{mandatoryItems.map(renderItem)}</ul>
            </div>

            <div className="pt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Optional
              </p>
              <ul className="space-y-2">
                {optionalItems.length > 0 ? (
                  optionalItems.map(renderItem)
                ) : (
                  <li className="text-sm text-gray-500">No optional forms.</li>
                )}
              </ul>
            </div>

            <div className="flex gap-2 pt-2">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomItem();
                  }
                }}
                placeholder="Add custom form…"
                className="flex-1 rounded-md border border-gray-300 text-sm px-2 py-1.5 focus:border-blue-700 focus:ring-blue-700"
              />
              <button
                type="button"
                onClick={addCustomItem}
                disabled={!draft.trim()}
                className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Add item
              </button>
            </div>
          </>
        )}

        <div className="flex justify-end pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !selectedId || !dealType}
            className="px-5 py-2 rounded-md bg-blue-900 text-white text-sm font-medium hover:bg-blue-950 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save compliance"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListingComplianceTab;
